import { nextTick } from 'vue'

export function useStructuredData() {
  
  const generateCaseStudySchema = (caseStudyData) => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': ['CreativeWork', 'Article', 'WebPage'],
      '@id': `https://www.ignis.co.uk${caseStudyData.path || ''}#casestudy`,
      name: caseStudyData.title,
      headline: caseStudyData.title,
      description: caseStudyData.description,
      abstract: caseStudyData.description,
      author: {
        '@type': 'Organization',
        '@id': 'https://www.ignis.co.uk/#organization',
        name: 'Ignis Ltd',
        url: 'https://www.ignis.co.uk',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'St. John\'s Hall, 374 North End Road',
          addressLocality: 'London',
          postalCode: 'SW6 1LY',
          addressCountry: 'GB'
        },
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+44 (0) 207 385 6677',
          contactType: 'Customer Service',
          email: 'jackie@ignis.co.uk'
        },
        sameAs: [
          'https://www.linkedin.com/company/ignis-ltd/',
          'https://www.instagram.com/ignisagency',
          'https://www.tiktok.com/@ignisagency'
        ],
        knowsAbout: [
          'Experiential Marketing',
          'Brand Experience',
          'Digital Marketing',
          'Consumer Engagement'
        ]
      },
      publisher: {
        '@id': 'https://www.ignis.co.uk/#organization'
      },
      about: [{
        '@type': 'Brand',
        name: caseStudyData.client,
        description: caseStudyData.clientDescription,
        additionalType: 'https://schema.org/Brand'
      }, {
        '@type': 'Event',
        name: caseStudyData.title,
        description: caseStudyData.description,
        startDate: caseStudyData.campaignStartDate,
        endDate: caseStudyData.campaignEndDate,
        audience: {
          '@type': 'Audience',
          audienceType: caseStudyData.targetAudience
        }
      }],
      datePublished: caseStudyData.datePublished || new Date().toISOString(),
      dateCreated: caseStudyData.dateCreated || caseStudyData.datePublished || new Date().toISOString(),
      keywords: caseStudyData.keywords || [],
      articleSection: 'Case Studies',
      genre: ['Marketing', 'Advertising', 'Brand Experience'],
      inLanguage: 'en-GB',
      isAccessibleForFree: true,
      mainEntity: {
        '@type': 'Service',
        name: 'Brand Experience & Marketing Campaign',
        description: `Professional ${caseStudyData.serviceType || 'experiential marketing'} services`,
        provider: {
          '@id': 'https://www.ignis.co.uk/#organization'
        },
        areaServed: caseStudyData.marketsCovered || ['United Kingdom'],
        serviceType: caseStudyData.serviceType || 'Experiential Marketing'
      },
      mentions: caseStudyData.brandMentions || [caseStudyData.client],
      citation: caseStudyData.citations || []
    }

    // Add results/metrics as mentions if available
    if (caseStudyData.results && caseStudyData.results.length > 0) {
      schema.mentions = [
        ...(schema.mentions || []),
        ...caseStudyData.results.map(result => ({
          '@type': 'QuantitativeValue',
          name: result.metric,
          value: result.value,
          description: result.description
        }))
      ]
    }

    // Add awards if available
    if (caseStudyData.awards && caseStudyData.awards.length > 0) {
      schema.award = caseStudyData.awards.map(award => award.name || award)
    }

    // Add images/media if available
    if (caseStudyData.images && caseStudyData.images.length > 0) {
      schema.image = caseStudyData.images
    }

    if (caseStudyData.video) {
      schema.video = {
        '@type': 'VideoObject',
        name: `${caseStudyData.title} - Case Study Video`,
        contentUrl: caseStudyData.video,
        thumbnailUrl: caseStudyData.videoThumbnail
      }
    }

    return schema
  }

  const generateBreadcrumbSchema = (breadcrumbs) => {
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.name,
        item: crumb.url ? `https://www.ignis.co.uk${crumb.url}` : undefined
      }))
    }
  }

  const generateOrganizationSchema = () => {
    return {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': 'https://www.ignis.co.uk/#organization',
      name: 'Ignis Ltd',
      alternateName: 'Ignis Agency',
      url: 'https://www.ignis.co.uk',
      logo: 'https://www.ignis.co.uk/favicon.ico',
      description: 'We\'ve been a \'start-up\' since 1988 - Constantly challenging and punching for some of the world\'s most exciting brands. Through insight-driven creativity, we \'upstart\' brand experiences to earn attention, energise fandom and drive numbers you can\'t argue with.',
      foundingDate: '1988',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'St. John\'s Hall, 374 North End Road',
        addressLocality: 'London',
        postalCode: 'SW6 1LY',
        addressCountry: 'GB'
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+44 (0) 207 385 6677',
        contactType: 'Customer Service',
        email: 'jackie@ignis.co.uk'
      },
      sameAs: [
        'https://www.linkedin.com/company/ignis-ltd/',
        'https://www.instagram.com/ignisagency',
        'https://www.tiktok.com/@ignisagency'
      ],
      knowsAbout: [
        'Brand Experience',
        'Marketing Campaigns',
        'Digital Marketing',
        'Event Marketing',
        'Experiential Marketing',
        'Brand Strategy',
        'Creative Services'
      ]
    }
  }

  // '@type' can be a string or an array of types
  const sameType = (a, b) => JSON.stringify(a) === JSON.stringify(b)

  const addStructuredData = (schemaData) => {
    return nextTick(() => {
      // Remove existing structured data for this type
      const existingScripts = document.querySelectorAll('script[type="application/ld+json"]')
      existingScripts.forEach(script => {
        const data = JSON.parse(script.textContent)
        if (sameType(data['@type'], schemaData['@type'])) {
          script.remove()
        }
      })

      // Add new structured data
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.textContent = JSON.stringify(schemaData)
      document.head.appendChild(script)
    })
  }

  const removeStructuredData = (type) => {
    document.querySelectorAll('script[type="application/ld+json"]').forEach(script => {
      if (sameType(JSON.parse(script.textContent)['@type'], type)) {
        script.remove()
      }
    })
  }

  return {
    generateCaseStudySchema,
    generateBreadcrumbSchema,
    generateOrganizationSchema,
    addStructuredData,
    removeStructuredData
  }
}